// src/modules/operaciones/inventarios/hooks/useVistos.js
//
// Manejo de localStorage de los ajustes vistos/revisados.
import { useState, useEffect, useCallback } from "react"; // Hooks usados
import { CLAVE_VISTOS } from "../utils/constantes"; // Clave de localStorage

// Ajustes "vistos" en dos capas:
//  • sesión: solo en memoria; se vacía al cambiar filtros (limpiarSesion).
//  • permanentes: persisten en localStorage (CLAVE_VISTOS) hasta que el usuario los quite.
// Los ids se guardan como string para que coincidan tras el viaje por JSON/localStorage.
const leerPermanentes = () => {
  try {
    const crudo = JSON.parse(localStorage.getItem(CLAVE_VISTOS) || "[]"); // Lee y parsea los ids guardados
    return new Set(Array.isArray(crudo) ? crudo.map(String) : []); // Devuelve un Set de strings
  } catch {
    return new Set(); // JSON corrupto o almacenamiento no disponible
  }
};

// Hook que gestiona los ajustes vistos (sesión y permanentes)
export default function useVistos() {
  const [vistosSesion, setVistosSesion] = useState(() => new Set()); // Vistos solo en esta sesión
  const [vistosPermanentes, setVistosPermanentes] = useState(leerPermanentes); // Vistos permanentes cargados de localStorage

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_VISTOS, JSON.stringify([...vistosPermanentes])); // Persiste los permanentes al cambiar
    } catch {
      /* noop: almacenamiento no disponible */
    }
  }, [vistosPermanentes]);

  // Indica si un id fue visto en sesión o es permanente
  const estaVisto = useCallback(
    (id) => vistosSesion.has(String(id)) || vistosPermanentes.has(String(id)),
    [vistosSesion, vistosPermanentes]
  );
  const esPermanente = useCallback((id) => vistosPermanentes.has(String(id)), [vistosPermanentes]); // Indica si un id es permanente

  // Si ya estaba marcado devuelve el mismo Set → React no vuelve a renderizar.
  const marcarSesion = useCallback((id) => {
    const k = String(id); // Id como string
    setVistosSesion((prev) => (prev.has(k) ? prev : new Set(prev).add(k))); // Agrega el id solo si no estaba
  }, []);

  // Alterna un id entre revisado permanente y no revisado
  const togglePermanente = useCallback((id) => {
    const k = String(id); // Id como string
    setVistosPermanentes((prev) => {
      const sig = new Set(prev); // Copia el Set (inmutabilidad)
      if (!sig.delete(k)) sig.add(k); // Si no existía lo agrega; si existía ya fue quitado
      return sig; // Devuelve el nuevo Set
    });
  }, []);

  // Reemplaza por un Set vacío; si ya estaba vacío conserva el actual para no provocar un render de más.
  const limpiarSesion = useCallback(() => setVistosSesion((prev) => (prev.size ? new Set() : prev)), []);

  const totalPermanentes = vistosPermanentes.size; // Cantidad de ajustes revisados permanentes
  // Quita todas las marcas permanentes tras confirmar
  const limpiarPermanentes = useCallback(() => {
    if (totalPermanentes === 0) return; // Nada que limpiar
    if (!window.confirm(`¿Quitar la marca de revisado a ${totalPermanentes} ajuste(s)? Esta acción no se puede deshacer.`)) return; // Pide confirmación
    setVistosPermanentes(new Set()); // Vacía los permanentes
  }, [totalPermanentes]);

  // Valores y funciones que expone el hook
  return {
    estaVisto, esPermanente, marcarSesion, togglePermanente, limpiarSesion, limpiarPermanentes,
    totalSesion: vistosSesion.size, // Cantidad vista en sesión
    totalPermanentes, // Cantidad de permanentes
  };
}